import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button, buttonVariants } from 'components/shadcn/button';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'components/shadcn/alert-dialog';
import { ragApi } from '@/api/rag';
import { MatcherEditor } from './MatcherEditor';
import { DocumentsList } from './DocumentsList';
import { SourceDefinition, SourceType, Frequency } from '@/types';
import { useRTL } from '@/hooks/useRTL';

export const SourceEditor = ({ source }: { source: SourceDefinition }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const queryClient = useQueryClient();
  const [showDocs, setShowDocs] = useState(false);

  const sourceTypeOptions: SourceType[] = useMemo(() => ['TEXT', 'URL', 'WEBSITE'], []);
  const frequencyOptions: Frequency[] = useMemo(() => ['NONE', 'ONE_TIME', 'DAILY', 'WEEKLY'], []);

  const { register, handleSubmit, watch, setValue } = useForm<SourceDefinition>({
    defaultValues: source,
  });

  const type = watch('type');
  const frequency = watch('frequency');

  const updateMutation = useMutation({
    mutationFn: ragApi.updateSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag-sources'] });
      toast.success(t('dashboard.toasts.saved'));
    },
    onError: () => toast.error(t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ragApi.deleteSource(source.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rag-sources'] }),
    onError: () => toast.error(t('common.error')),
  });

  const matcherMutation = useMutation({
    mutationFn: () => ragApi.createMatcher(source.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rag-sources'] }),
    onError: () => toast.error(t('common.error')),
  });

  const onSave = (data: SourceDefinition) => {
    updateMutation.mutate(data);
  };

  return (
    <div className="rounded-md border p-4 space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Actions */}
      <div className="flex flex-row items-center gap-3">
        <div className="flex-grow" />
        <Button
          variant="secondary"
          onClick={handleSubmit(onSave)}
          disabled={updateMutation.isPending}
        >
          {t('common.save')}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" disabled={deleteMutation.isPending}>
              {t('common.delete')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.areYouSure')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('dashboard.chatForm.rag.deleteSourceWarning')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate()}
                className={buttonVariants({ variant: 'destructive' })}
              >
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('dashboard.chatForm.rag.type')}</Label>
          <Select
            value={type}
            onValueChange={(val) => setValue('type', val as SourceType, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sourceTypeOptions.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('dashboard.chatForm.rag.frequency')}</Label>
          <Select
            value={frequency}
            onValueChange={(val) => setValue('frequency', val as Frequency, { shouldDirty: true })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {frequencyOptions.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 col-span-1 md:col-span-2">
          <Label>{t('dashboard.chatForm.rag.url')}</Label>
          <Input placeholder="https://example.com" {...register('url')} />
        </div>

        {type === 'WEBSITE' && (
          <div className="space-y-2 col-span-1 md:col-span-2">
            <Label>{t('dashboard.chatForm.rag.linkSelector')}</Label>
            <Input placeholder="e.g. main a" {...register('linkSelector')} />
          </div>
        )}

        <div className="space-y-2">
          <Label>{t('dashboard.chatForm.rag.chunkSize')}</Label>
          <Input type="number" {...register('chunkSize', { valueAsNumber: true })} />
        </div>
        <div className="space-y-2">
          <Label>{t('dashboard.chatForm.rag.chunksOverlap')}</Label>
          <Input type="number" {...register('chunksOverlap', { valueAsNumber: true })} />
        </div>
        <div className="space-y-2 col-span-1 md:col-span-2">
          <Label>{t('dashboard.chatForm.rag.splitDelimiter')}</Label>
          <Input placeholder={t('dashboard.chatForm.rag.splitDelimiterPlaceholder')} {...register('splitDelimiter')} />
        </div>
      </div>

      {/* Matchers */}
      <div className="border-t pt-4">
        <div className="flex flex-row justify-between items-center mb-2">
          <Label className="font-semibold">{t('dashboard.chatForm.rag.matchers')}</Label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => matcherMutation.mutate()}
            disabled={matcherMutation.isPending}
          >
            {t('dashboard.chatForm.rag.addMatcher')}
          </Button>
        </div>

        <div className="space-y-2">
          {!source.matchers?.length && (
            <div className="text-sm text-muted-foreground italic">
              {t('dashboard.chatForm.rag.noMatchers')}
            </div>
          )}
          {source.matchers?.map((matcher) => <MatcherEditor key={matcher.id} matcher={matcher} />)}
        </div>
      </div>

      {/* Documents */}
      <div className="border-t pt-4">
        <Button variant="link" className="p-0 h-auto" onClick={() => setShowDocs(!showDocs)}>
          {showDocs
            ? t('dashboard.chatForm.rag.hideDocuments')
            : t('dashboard.chatForm.rag.showDocuments')}
        </Button>
        {showDocs && <DocumentsList sourceId={source.id} />}
      </div>
    </div>
  );
};
